import { forwardRef, useContext, useMemo } from 'react';
import { isElement } from 'react-is';

import { _canReceiveDefaultTextFormat } from '@dynatrace/strato-components/core';
import { Flex } from '@dynatrace/strato-components/layouts';
import { Text } from '@dynatrace/strato-components/typography';

import { NotifyButtonContext } from '../../../buttons/notify-button/contexts/NotifyButtonContext.js';
import { NotificationSettingsContext } from '../contexts/NotificationSettingsContext.js';
import { useNotificationMessages } from '../hooks/useNotificationMessages.js';
import * as styles from '../NotificationSettings.sty.js';
import type { NotificationSettingsProps } from '../NotificationSettings.js';

/**
 *
 * @internal
 */
export const Content = forwardRef<
  HTMLDivElement,
  Readonly<{
    children?: NotificationSettingsProps['children'];
  }>
>((props, ref) => {
  const { children, ...remainingProps } = props;
  const { overlayAriaLabelledBy } = useContext(NotifyButtonContext);

  const { enabled, hasReadPermission, hasWritePermission } = useContext(
    NotificationSettingsContext,
  );

  const { headingText, infoText } = useNotificationMessages({
    notificationEnabled: enabled,
  });

  // Determine if a render function or a component is passed.
  const childrenContent = useMemo(() => {
    if (typeof children === 'function') {
      return children({
        disabled: !enabled,
        headingText,
        infoText,
        hasReadPermission,
        hasWritePermission,
      });
    }
    return children;
  }, [
    children,
    enabled,
    hasReadPermission,
    hasWritePermission,
    headingText,
    infoText,
  ]);

  // If no children are provided, we can default our own values.
  if (children === undefined) {
    return (
      <Flex ref={ref} flexDirection="column" gap={0} {...remainingProps}>
        <Text id={overlayAriaLabelledBy} textStyle="base-emphasized">
          {headingText}
        </Text>

        <Text textStyle="small" className={styles.overlayText}>
          {infoText}
        </Text>
      </Flex>
    );
  }

  if (_canReceiveDefaultTextFormat(childrenContent)) {
    return (
      <Text
        id={overlayAriaLabelledBy}
        ref={ref}
        as="div"
        textStyle="base"
        {...remainingProps}
      >
        {childrenContent}
      </Text>
    );
  }

  if (isElement(childrenContent)) {
    return childrenContent;
  }

  return (
    <div ref={ref} {...remainingProps}>
      {childrenContent}
    </div>
  );
});
